import { useMemo, useRef, useState } from 'react';
import { useThree, useFrame, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore, type Entity } from '../state/store';
import { useTool } from '../state/store';
import { commitEntityEdit } from '../tools/cad/entityGeometry';
import { constrainDraftPoint, useSnapStore } from '../tools/snapping';
import { pixelsToWorld } from '../tools/snapping/SnapManager';
import { GROUND_PLANE } from '../core/scene';
import { useConstraintStore } from '../core/constraints/constraintStore';
import { solveConstraintSet, syncBoundDimensions } from '../core/constraints/solve';
import { MARKER_CENTER, MARKER_RADIUS, type ConstraintRef } from '../core/constraints/types';
import { useSystemVarsStore } from '../state/useSystemVarsStore';
import { useGripDragStore } from '../state/useGripDragStore';
import { SELECTION_COLORS, UX_COLORS } from '../themes/colors';

/**
 * OCS grip_solve_anchor_refs parity (Faz 6): the dragged grip's ref is the
 * solve anchor (drives the constraints) — a Fixed-held point can never
 * become the driver because pinned particles ignore the anchor flag.
 */
function gripMarkerRef(entity: Entity, grip: Grip): ConstraintRef | null {
    switch (grip.field) {
        case 'start': return { entityId: entity.id, marker: entity.type === 'rectangle' ? 0 : 0 };
        case 'end': return { entityId: entity.id, marker: entity.type === 'rectangle' ? 2 : 1 };
        case 'center': return { entityId: entity.id, marker: MARKER_CENTER };
        case 'radiusPoint': return { entityId: entity.id, marker: MARKER_RADIUS };
        case 'vertex':
            return grip.index !== undefined && grip.index >= 0
                ? { entityId: entity.id, marker: grip.index }
                : null;
        default:
            return null; // textPosition is not constraint geometry
    }
}

/**
 * Grip editing for selected CAD entities (docs/plans/2d plan Faz 1.1).
 *
 * Classic 2D contract: a selected entity shows blue square grips at
 * its defining points. Dragging a grip live-moves just that point (rubber
 * band feedback); release commits ONE undoable step. Clicking (no drag) an
 * endpoint grip of a line/polyline adds a vertex there — the same click
 * semantics the real product uses; kept minimal and safe.
 *
 * Mounted inside ToolScope so it participates in the normal pointer flow.
 * Only active for the 'select' tool; any running command owns the pointer
 * before this layer (grips are hidden while a command interaction is on).
 */

interface Grip {
    /** stable key within the entity */
    id: string;
    entityId: string;
    /** which data field the grip edits */
    field: 'start' | 'end' | 'center' | 'vertex' | 'radiusPoint' | 'textPosition';
    /** vertex index for polyline/polygon grips */
    index?: number;
    position: [number, number, number];
}

/** Extract the editable grip set of one entity. */
function gripsOf(e: Entity): Grip[] {
    const d = e.data as Record<string, any>;
    const out: Grip[] = [];
    const push = (field: Grip['field'], position: number[] | undefined, index?: number) => {
        if (!Array.isArray(position) || position.length < 2) return;
        out.push({
            id: `${e.id}:${field}:${index ?? ''}`,
            entityId: e.id,
            field,
            index,
            position: [position[0], position[1], position[2] ?? 0] as [number, number, number],
        });
    };
    switch (e.type) {
        case 'line':
            push('start', d.start);
            push('end', d.end);
            break;
        case 'circle':
            push('center', d.center);
            // Radius grip: east rim point (drag changes the radius).
            if (Array.isArray(d.center) && typeof d.radius === 'number') {
                out.push({
                    id: `${e.id}:radius`, entityId: e.id, field: 'radiusPoint', index: -1,
                    position: [d.center[0] + d.radius, d.center[1], d.center[2] ?? 0],
                });
            }
            break;
        case 'rectangle':
            push('start', d.corner1);
            push('end', d.corner2);
            break;
        case 'polyline':
        case 'polygon':
            (Array.isArray(d.vertices) ? d.vertices : []).forEach((v: number[], i: number) => push('vertex', v, i));
            break;
        case 'text':
        case 'mtext':
            push('textPosition', d.position);
            break;
        default:
            break;
    }
    return out;
}

/** Apply a grip drag delta to the entity's data bag (pure). */
function moveGrip(
    data: Record<string, any>,
    grip: Grip,
    target: [number, number, number],
    source: [number, number, number]
): Record<string, any> {
    const d = { ...data };
    switch (grip.field) {
        case 'start': d.start = target; break;
        case 'end': d.end = target; break;
        case 'center': d.center = target; break;
        case 'textPosition': d.position = target; break;
        case 'radiusPoint': {
            const c = d.center as number[];
            if (Array.isArray(c)) d.radius = Math.max(1e-4, Math.hypot(target[0] - c[0], target[1] - c[1]));
            break;
        }
        case 'vertex': {
            const vs = [...(d.vertices as number[][])];
            if (grip.index !== undefined && vs[grip.index]) {
                // delta move keeps the vertex's level; other vertices unchanged
                vs[grip.index] = target;
                d.vertices = vs;
            }
            break;
        }
    }
    void source;
    return d;
}

/** Re-render threshold for zoom-driven size changes (relative fraction). */
const GRIP_SIZE_EPSILON = 0.08;
/** Classic minimum grip box: 8px (the PICKBOX-driven pixel-fixed contract). */
const GRIP_MIN_PX = 8;

/** Grip visual state (Faz 11.6, OCS three-state parity). */
type GripState = 'unselected' | 'hover' | 'hot';

/** Half-extent in screen pixels: max(PICKBOX×2, 8) / 2. */
function gripHalfPx(pickbox: number): number {
    return Math.max(pickbox * 2, GRIP_MIN_PX) / 2;
}

function GripMarker({ grip, state, onDown }: {
    grip: Grip;
    state: GripState;
    onDown: (g: Grip, e: ThreeEvent<PointerEvent>) => void;
}) {
    const { camera, gl } = useThree();
    const pickbox = useSystemVarsStore((s) => s.PICKBOX);
    const [hovered, setHovered] = useState(false);
    const [half, setHalf] = useState(0.12);

    // Pixel-fixed grip: the world half-extent is re-derived per frame behind
    // an epsilon guard, so zooming never grows/shrinks the grip on screen.
    useFrame(() => {
        const at = new THREE.Vector3(grip.position[0], grip.position[1], grip.position[2]);
        const next = pixelsToWorld(gripHalfPx(pickbox), at, camera, gl);
        setHalf((prev) => (
            Math.abs(prev - next) / Math.max(next, 1e-6) > GRIP_SIZE_EPSILON ? next : prev
        ));
    });

    const active: GripState = state === 'hot' ? 'hot' : hovered ? 'hover' : 'unselected';
    const outline = active === 'unselected'
        ? SELECTION_COLORS.highlight
        : active === 'hover'
            ? SELECTION_COLORS.window
            : UX_COLORS.gripHot;
    const square = useMemo(() => [
        [-half, -half, 0], [half, -half, 0],
        [half, half, 0], [-half, half, 0],
        [-half, -half, 0],
    ] as [number, number, number][], [half]);

    return (
        <group position={grip.position}>
            {/* Invisible larger hit box keeps small grips easy to grab. */}
            <mesh
                onPointerDown={(e) => { e.stopPropagation(); onDown(grip, e); }}
                onPointerOver={(e) => {
                    e.stopPropagation();
                    setHovered(true);
                    // Grip hover speaks the move affordance (Faz 11.6).
                    useAppStore.getState().setCursor('move');
                }}
                onPointerOut={() => {
                    setHovered(false);
                    useAppStore.getState().setCursor('default');
                }}
                onPointerMove={(e) => e.stopPropagation()}
                userData={{ isGrip: true }}
            >
                <boxGeometry args={[half * 3, half * 3, half]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>
            {/* Hot grip fills solid red; idle/hover stay hollow squares. */}
            {active === 'hot' && (
                <mesh position={[0, 0, -0.001]}>
                    <planeGeometry args={[half * 2, half * 2]} />
                    <meshBasicMaterial color={outline} transparent opacity={0.85} depthWrite={false} />
                </mesh>
            )}
            <Line points={square} color={outline} lineWidth={2} />
        </group>
    );
}

export function GripEditLayer() {
    const tool = useTool();
    const selectedIds = useAppStore((s) => s.selectedIds);
    const entities = useAppStore((s) => s.entities);
    const gridSize = useSnapStore((s) => s.gridSize);
    void gridSize;
    const { camera, gl, raycaster, pointer, scene } = useThree();

    // Live-drag preview state kept in a ref + a single dirty counter so
    // pointermove storms re-render only the moving rubber band, not all grips.
    const dragRef = useRef<{ grip: Grip; from: [number, number, number]; cursor: THREE.Vector3 | null } | null>(null);
    // Hot (dragged) grip id — state, so the red-filled hot square renders.
    const [hotGripId, setHotGripId] = useState<string | null>(null);

    const grips = useMemo(() => {
        if (tool !== 'select') return [];
        return selectedIds.flatMap((id) => {
            const e = entities[id];
            if (!e || e.locked || !e.visible) return [];
            return gripsOf(e);
        });
    }, [tool, selectedIds, entities]);

    const groundAt = (): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    };

    const handleGripDown = (grip: Grip, _e: ThreeEvent<PointerEvent>) => {
        const ent = entities[grip.entityId];
        if (!ent) return;
        setHotGripId(grip.id);
        // Snapshot the original data bag once: the live preview mutates the
        // store without history, and the final commit restores it first so
        // the undo step records before = original (not preview state).
        const origData = structuredClone(ent.data) as Record<string, any>;
        dragRef.current = { grip, from: [...grip.position] as [number, number, number], cursor: null };
        // Capture-phase window listeners own the drag gesture (the canvas
        // keeps rendering the preview through the store writes below).
        const onMove = (ev: PointerEvent) => {
            const rect = gl.domElement.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1
            );
            raycaster.setFromCamera(ndc, camera);
            const hit = raycaster.ray.intersectPlane(GROUND_PLANE, new THREE.Vector3());
            if (!hit || !dragRef.current) return;
            const snapped = constrainDraftPoint(hit, camera, gl, null);
            dragRef.current.cursor = snapped;
            // Cursor-anchored readout balloon (Faz 11.6): the DOM ghost rides
            // the pointer with the constrained world point.
            useGripDragStore.getState().setDrag(
                { x: ev.clientX, y: ev.clientY },
                [snapped.x, snapped.y, snapped.z]
            );
            // Live-preview the entity edit itself (uncommitted).
            const e2 = useAppStore.getState().entities[grip.entityId];
            if (e2) {
                const next = moveGrip(
                    structuredClone(e2.data),
                    grip,
                    [snapped.x, snapped.y, snapped.z],
                    dragRef.current.from
                );
                useAppStore.setState((st) => ({ entities: { ...st.entities, [grip.entityId]: { ...e2, data: next } } }));
            }
        };
        const onUp = (ev: PointerEvent) => {
            window.removeEventListener('pointermove', onMove, true);
            window.removeEventListener('pointerup', onUp, true);
            // Readout balloon ends with the drag (Faz 11.6).
            useGripDragStore.getState().endDrag();
            useAppStore.getState().setCursor('default');
            setHotGripId(null);
            const drag = dragRef.current;
            dragRef.current = null;
            if (!drag) return;
            const rect = gl.domElement.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1
            );
            raycaster.setFromCamera(ndc, camera);
            const hit = raycaster.ray.intersectPlane(GROUND_PLANE, new THREE.Vector3());
            if (!hit) return;
            const target3 = constrainDraftPoint(hit, camera, gl, null);
            const moved = Math.hypot(target3.x - drag.from[0], target3.y - drag.from[1]) > 1e-6;
            if (!moved) {
                // Click without drag: restore the original data (preview may
                // have nudged it) and leave history untouched.
                const cur = useAppStore.getState().entities[grip.entityId];
                if (cur) useAppStore.getState().updateEntity(grip.entityId, { data: origData });
                return;
            }
            // Restore the original bag first, then commit as ONE undoable
            // step so history before/after are exact (preview writes are
            // never captured into history).
            const cur = useAppStore.getState().entities[grip.entityId];
            if (cur) useAppStore.getState().updateEntity(grip.entityId, { data: origData });
            const original = [...drag.from] as [number, number, number];
            const target = [target3.x, target3.y, target3.z] as [number, number, number];
            commitEntityEdit([grip.entityId], (data) => moveGrip(data, { ...grip, position: original }, target, original));
            // Anchor-locked re-solve (Faz 6): the dragged grip stays where
            // the user dropped it; connected constraints follow.
            if (ent && useConstraintStore.getState().set.constraints.length > 0) {
                const anchor = gripMarkerRef(ent, grip);
                solveConstraintSet(anchor ? { anchorRefs: [anchor] } : undefined);
                syncBoundDimensions();
            }
        };
        window.addEventListener('pointermove', onMove, true);
        window.addEventListener('pointerup', onUp, true);
    };

    if (tool !== 'select' || grips.length === 0) return null;

    return (
        <group>
            {grips.map((g) => (
                <GripMarker
                    key={g.id}
                    grip={g}
                    state={hotGripId === g.id ? 'hot' : 'unselected'}
                    onDown={handleGripDown}
                />
            ))}
        </group>
    );
}
